import {Component} from '@angular/core';
import {Localization} from '../../../common/enums/localization';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ContactsService} from "../../../common/services/contacts.service";
import {Destroyable, takeUntilDestroyed} from "../../../../angular/component/destruction/destroyable.interface";
import {combineLatest, Observable, Subject} from 'rxjs';
import {SelfDestruct} from "../../../../angular/component/destruction/self-destruct";
import {filter, map, shareReplay, switchMap, takeUntil, tap} from "rxjs/operators";
import {IdAndLabel} from "../../../common/model/id-and-label";
import {trackLoadingOn} from "../../../../angular/component/loading/loads-data";
import {StructureContactType, StructureType} from "../../../common/enums/enums";
import {BuildingsContactsService} from "../../../common/services/buildings-contacts.service";
import {BuildingsService, ResidenzFloor} from "../../../common/services/buildings.service";
import {NotificationForm} from "../notificationForm";
import {NotificationsService} from "../notifications.service";
import {MessageType, MessagingCenterService} from "../../../common/services/messaging-center.service";
import {SnackBarService} from "../../../../services/snackbar.service";
import {TranslateService} from "@ngx-translate/core";
import {BuildingContactLocation} from "../../../common/model/building-contact-location";
import {SelectTreeItem} from "@npm.chapps.com/chapps.common.angular.library/lib/model/select-tree-item";
import {MatDialogRef} from '@angular/material/dialog';

@Component({
  selector: 'app-new-resident-notification-dialog',
  templateUrl: './new-resident-notification-dialog.component.html',
  styleUrls: ['./new-resident-notification-dialog.component.scss']
})
export class NewResidentNotificationDialogComponent implements Destroyable {
  public readonly destroyed$ = new Subject<NewResidentNotificationDialogComponent>();

  public readonly Localization = Localization;
  public readonly notificationForm = new UntypedFormGroup({
    title: new UntypedFormControl('', [Validators.required]),
    contactId: new UntypedFormControl('', [Validators.required]),
    buildingId: new UntypedFormControl({value: null, disabled: true}, [Validators.required]),
    location: new UntypedFormControl({value: null, disabled: true}),
    description: new UntypedFormControl(''),
    source: new UntypedFormControl(''),
  });
  private floors: ResidenzFloor[];

  public get contactField() { return this.notificationForm.get('contactId') as UntypedFormControl; }
  public readonly selectedContactId$ = this.contactField.valueChanges.pipe(filter(Boolean)) as Observable<number>;

  public get buildingField() { return this.notificationForm.get('buildingId') as UntypedFormControl; }
  public readonly selectedBuildingId$ = this.buildingField.valueChanges.pipe(
      filter(Boolean),
  ) as Observable<number>;

  public get locationField() { return this.notificationForm.get('location') as UntypedFormControl; }


  public loadingResidents = false;
  public readonly contact$ =
      trackLoadingOn(
    this.contacts.getContactsOfType(StructureContactType.TENANT, StructureContactType.OWNER, StructureContactType.JANITOR),
    this, 'loadingResidents'
  );

  public readonly contactOptions$ = this.contact$.pipe(
    map(contacts => contacts.content.map(contactAsOption)),
    takeUntil(this.destroyed$),
  );



  public loadingLocations = false;
  public readonly locationOptions$ = this.selectedContactId$.pipe(
      switchMap(contactId => trackLoadingOn(
          this.contacts.getLocations(contactId),
          this, 'loadingLocations'
      )),
      tap((locations) => this.didLoadLocations(locations)),
      takeUntilDestroyed(this),
      shareReplay()
  );

  public loadingBuildings = false;
  public readonly buildingOptions$ = this.locationOptions$.pipe(
      map(locations => {
        const uniqueBuildingIds = new Set<number>(locations.map(location => location.buildingId));
        const buildings = Array.from(uniqueBuildingIds).map(id => locations.find(location => location.buildingId === id));
        return buildings.map(({buildingId, buildingLabel}) => new IdAndLabel(buildingId, buildingLabel));
      }),
      tap(buildings => this.didLoadBuildings(buildings))
  );

  public readonly buildingLocations$ = this.selectedBuildingId$.pipe(
      switchMap(buildingId => this.buildingService.getResidenzLocations(buildingId)),
      tap(floors => this.floors = floors),
      map(floors  => floors.map(({id, label, locations}) =>
          ({ id, label, options: locations} as SelectTreeItem))
      ),
  )


  public readonly selectedContactBuildings$ = combineLatest([this.locationOptions$, this.buildingOptions$]).pipe(
      map(([locations, buildings]) => buildings.filter(({id}) => locations.some(location => location.buildingId === id)))
  );

  public creatingNotification = false;

  public get hasContactSelected() {
    return this.contactField.valid;
  }
  constructor(
    public readonly self: MatDialogRef<NewResidentNotificationDialogComponent>,
    private readonly contacts: ContactsService,
    private readonly buildingContacts: BuildingsContactsService,
    private readonly buildingService: BuildingsService,
    private readonly notificationsService: NotificationsService,
    private readonly messagingCenter: MessagingCenterService,
    private readonly snackbarService: SnackBarService,
    private readonly translate: TranslateService,
  ) {
    this.contactField.valueChanges
        .pipe(takeUntilDestroyed(this)).subscribe((contactId: number) => this.didChangeContactSelection(contactId));
      this.buildingField.valueChanges
        .pipe(takeUntilDestroyed(this))
        .subscribe((buildingId) => this.didChangeBuildingSelection(buildingId));

      this.locationOptions$.subscribe()
  }

  public ngOnDestroy(): void {
      SelfDestruct(this);
  }

  public cancel() {
    this.self.close();
  }

  public complete() {
    if (this.validate()) {
      const form = new NotificationForm(this.notificationForm);
      trackLoadingOn(
          this.notificationsService.create(form.toModel()),
          this, 'creatingNotification'
      ).subscribe(() => this.didCreateNotification());
    }
  }

  private validate() {
    Object.keys(this.notificationForm.controls).forEach(control => {
      const field = this.notificationForm.get(control);
      if (field instanceof UntypedFormControl) {
        field.markAsTouched({onlySelf: true})
      }
    });
    return this.notificationForm.valid;
  }

  private didCreateNotification() {
    this.self.close(true);
    this.snackbarService.show(this.translate.instant(Localization.ResidentNotificationAddedMessage));
    this.messagingCenter.reloadMessages(MessageType.ResidenzNotifications);
  }

  private didLoadBuildings(buildings: IdAndLabel[]) {
    if (buildings.length === 1) {
      this.buildingField.setValue(buildings[0].id, {emitEvent: true});
    }
  }

  private didLoadLocations(locations: BuildingContactLocation[]) {
    this.targetId = locations[0]?.structureId;
    this.notificationForm.controls.buildingId.reset();
  }

  private didChangeContactSelection(contactId: number|null) {
    this.locationField.reset(null);
  }

  private didChangeBuildingSelection(buildingId: number | null) {
    !!buildingId ? this.locationField.enable() : this.locationField.disable();
    this.locationField.reset(null, {emitEvent: true});
  }

  protected readonly StructureType = StructureType;
  public targetId: number;
}

const contactAsOption = ({id, firstName, lastName}) => new IdAndLabel(id, `${firstName} ${lastName}`);
